/**
 * 流程模板配置（可视化节点 + 条件分支）
 *
 * 画布用「横向节点卡片 + 分支连线」的方式呈现，而不是图形化拖拽：
 * 财务/技术实施人员看得懂节点语义（谁审批、超时多久、什么条件走哪条路），
 * 但不需要学习拖拽操作，也避免拖错连线产生难以排查的流程死循环。
 *
 * 一条硬约束在页面上反复强调：**财务初审是人工节点，不可删除、不可改成自动通过**。
 * 条件分支能改变的只是「谁来审、审几级」，不能把人工确认环节去掉。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  AuditOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  SafetyOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  BIZ_TYPE_LABELS,
  BizType,
  DEFAULT_ROLES,
  FLOW_BACKED_BIZ_TYPES,
  FLOW_CONDITION_FIELDS_BY_BIZ,
  FLOW_NODE_TYPE_LABELS,
  FlowNodeType,
  INVOICE_TYPE_LABELS,
  PermissionCode,
  RISK_LEVEL_LABELS,
  type FlowNode,
  type FlowTemplate,
  type FlowTemplateNodeInput,
} from '@ilc/shared';
import { flowApi } from '@/api/endpoints';
import { mockFlowTemplateList } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

/** 角色码 → 角色名称（供节点卡片与下拉复用） */
const ROLE_OPTIONS = DEFAULT_ROLES.map((role) => ({ value: role.code as string, label: role.name }));
const ROLE_LABELS: Record<string, string> = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));

const NODE_ICONS: Record<FlowNodeType, React.ReactNode> = {
  START: <PlayCircleOutlined style={{ color: '#389e0d' }} />,
  AUTO: <ApiOutlined style={{ color: '#1677ff' }} />,
  APPROVAL: <AuditOutlined style={{ color: '#d46b08' }} />,
  CONDITION: <BranchesOutlined style={{ color: '#722ed1' }} />,
  PUSH: <SendOutlined style={{ color: '#0958d9' }} />,
  END: <CheckCircleOutlined style={{ color: '#389e0d' }} />,
};

/**
 * 条件字段 → 中文名。
 *
 * **哪些字段能用，由模板的业务类型决定**（`FLOW_CONDITION_FIELDS_BY_BIZ`），
 * 这里只负责把字段名翻译成人话。两份清单各司其职：
 * 引擎按变量名取值，名字对不上就是"这条规则永远为假"——而界面上看不出异常，
 * 所以下拉里**不能**出现某个业务类型根本没有的变量。
 * （这里原先有一个 `supplierType`，四个流程里都没有这个变量：选了它配的条件
 * 一条都不会命中，服务端保存校验现在也拦得住。）
 */
const CONDITION_FIELD_LABELS: Record<string, string> = {
  amount: '金额',
  riskLevel: '风险等级',
  invoiceType: '发票类型',
  contractType: '合同类型',
  expenseType: '费用类型',
};

/** 模板的业务类型 → 可用的条件字段（引擎侧变量的白名单） */
function conditionFieldsOf(bizType: string | undefined): string[] {
  return [...(FLOW_CONDITION_FIELDS_BY_BIZ[bizType ?? ''] ?? [])];
}

/**
 * 新建模板时的起始节点骨架：开始 → 一个审批节点 → 结束。
 *
 * 不给"空白模板"：一个连起止节点都没有的模板保存不了（保存校验要求恰好一个
 * 开始节点、至少一个结束节点），而管理员第一次进这个页面多半只是想先有个能跑的
 * 流程再改。审批节点按业务类型预置了默认流程里的角色与权限——
 * 发票那套还带着「财务初审」这个红线节点，否则保存就会被打回。
 */
const STARTER_APPROVALS: Record<string, { name: string; role: string; permission: string; description: string }> = {
  [BizType.INVOICE]: {
    name: '财务初审',
    role: 'FINANCE_REVIEWER',
    permission: PermissionCode.INVOICE_REVIEW,
    description: '审阅自动化校验报告，对识别异常、校验风险点人工复核判断。发票必须经人工确认后才能推送 T+。',
  },
  [BizType.CONTRACT]: {
    name: '经营部审核',
    role: 'BUSINESS_HANDLER',
    permission: PermissionCode.CONTRACT_REVIEW,
    description: '审核合同条款、供应商资质与商务合理性。',
  },
  [BizType.EXPENSE_APPLY]: {
    name: '部门负责人审批',
    role: 'DEPT_HEAD',
    permission: PermissionCode.EXPENSE_DEPT_APPROVE,
    description: '业务真实性审核：这笔费用是否确有需要、金额是否合理。',
  },
  [BizType.EXPENSE_REIMBURSE]: {
    name: '部门负责人审批',
    role: 'DEPT_HEAD',
    permission: PermissionCode.EXPENSE_DEPT_APPROVE,
    description: '业务真实性审核；部门负责人的数据范围是本部门。',
  },
};

function starterNodes(bizType: string): FlowTemplateNodeInput[] {
  const approval = STARTER_APPROVALS[bizType] ?? STARTER_APPROVALS[BizType.INVOICE]!;
  return [
    { key: 'start', name: '开始', type: FlowNodeType.START, sortOrder: 1, nextKey: 'approve' },
    {
      key: 'approve',
      name: approval.name,
      type: FlowNodeType.APPROVAL,
      sortOrder: 2,
      assigneeRoleCode: approval.role,
      requiredPermission: approval.permission,
      timeoutHours: 24,
      nextKey: 'end',
      description: approval.description,
    },
    { key: 'end', name: '结束', type: FlowNodeType.END, sortOrder: 3 },
  ];
}

const OPERATOR_LABELS: Record<string, string> = {
  GT: '大于',
  GTE: '大于等于',
  LT: '小于',
  LTE: '小于等于',
  EQ: '等于',
  IN: '属于',
  NOT_IN: '不属于',
};

/** 把条件表达式翻译成一句人话，避免管理员去读 JSON */
function describeCondition(node: FlowNode): string {
  const condition = node.condition;
  if (!condition || condition.rules.length === 0) return '无条件（默认路径）';
  const joiner = condition.logic === 'AND' ? ' 且 ' : ' 或 ';
  return condition.rules
    .map((rule) => {
      const field = CONDITION_FIELD_LABELS[rule.field] ?? rule.field;
      const operator = OPERATOR_LABELS[rule.operator] ?? rule.operator;
      let value = Array.isArray(rule.value) ? rule.value.join('、') : String(rule.value);
      if (rule.field === 'amount') value = `¥${(Number(value) / 100).toLocaleString('zh-CN')}`;
      if (rule.field === 'riskLevel') value = RISK_LEVEL_LABELS[value as keyof typeof RISK_LEVEL_LABELS] ?? value;
      if (rule.field === 'invoiceType') value = INVOICE_TYPE_LABELS[value as keyof typeof INVOICE_TYPE_LABELS] ?? value;
      return `${field} ${operator} ${value}`;
    })
    .join(joiner);
}

/** 单个节点卡片 */
function NodeCard({
  node,
  bizType,
  onEdit,
  onRemove,
  onMove,
  canWrite,
  index,
}: {
  node: FlowNode;
  bizType: string | undefined;
  onEdit: () => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  canWrite: boolean;
  index: number;
}): React.ReactElement {
  const locked = node.type === FlowNodeType.AUTO || node.type === FlowNodeType.PUSH;
  /**
   * 发票的「人工确认节点」：**按处理权限判断，与它叫什么名字无关**。
   *
   * 服务端的红线也是按 `invoice:review` 这个权限码校验的（见
   * `flow.template.service.ts` 的 `REQUIRED_APPROVAL`）。这里从前按节点 key
   * （`finance_review`）判断——租户把节点改个名字，界面上就既不高亮、也"可以删除"，
   * 而服务端仍会拦下保存。两处判据必须一致，而且只有发票有这条约束。
   */
  const isReviewNode =
    bizType === BizType.INVOICE &&
    node.type === FlowNodeType.APPROVAL &&
    node.requiredPermission === PermissionCode.INVOICE_REVIEW;

  return (
    <Card
      size="small"
      style={{
        width: 250,
        borderColor: isReviewNode ? '#d46b08' : undefined,
        borderWidth: isReviewNode ? 2 : 1,
      }}
      styles={{ body: { padding: 10 } }}
    >
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Space wrap size={4}>
          {NODE_ICONS[node.type]}
          <Typography.Text strong>{node.name}</Typography.Text>
        </Space>
        <Space wrap size={4}>
          <Tag>{FLOW_NODE_TYPE_LABELS[node.type]}</Tag>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            #{index + 1} · key {node.key}
          </Typography.Text>
        </Space>

        {node.type === FlowNodeType.APPROVAL ? (
          <Space direction="vertical" size={2}>
            <Typography.Text style={{ fontSize: 12 }}>
              处理角色：{node.assigneeRoleCode ? ROLE_LABELS[node.assigneeRoleCode] ?? node.assigneeRoleCode : '未指定'}
            </Typography.Text>
            {node.requiredPermission ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                需权限：{node.requiredPermission}
              </Typography.Text>
            ) : null}
            {node.timeoutHours ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                超时提醒：{node.timeoutHours} 小时
              </Typography.Text>
            ) : null}
            {node.allowSpecialRelease ? (
              <Tag color="red" icon={<SafetyOutlined />}>
                允许特例放行
              </Tag>
            ) : null}
          </Space>
        ) : null}

        {node.type === FlowNodeType.CONDITION ? (
          <Space direction="vertical" size={2}>
            <Typography.Text style={{ fontSize: 12, color: '#722ed1' }}>{describeCondition(node)}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              命中 → {node.nextKey ?? '未指定'}；否则 → {node.defaultNextKey ?? '未指定'}
            </Typography.Text>
          </Space>
        ) : null}

        {node.description ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {node.description}
          </Typography.Text>
        ) : null}

        {isReviewNode ? (
          <Alert
            type="warning"
            showIcon
            style={{ padding: '4px 8px' }}
            message={<span style={{ fontSize: 11 }}>人工确认节点：不可删除、不可改为自动通过</span>}
          />
        ) : null}

        <Space size={4}>
          <Button size="small" type="link" disabled={!canWrite} onClick={onEdit}>
            编辑
          </Button>
          {locked || isReviewNode ? (
            <Tooltip
              title={
                isReviewNode
                  ? '发票必须保留一个「发票复核」权限的人工确认节点，按业务约束不允许删除'
                  : '系统节点（自动校验 / 推送 T+）由平台固定编排，不可删除'
              }
            >
              <Button size="small" type="link" disabled icon={<StopOutlined />}>
                删除
              </Button>
            </Tooltip>
          ) : (
            <Button size="small" type="link" danger icon={<DeleteOutlined />} disabled={!canWrite} onClick={onRemove}>
              删除
            </Button>
          )}
          <Button size="small" type="link" disabled={!canWrite || index === 0} onClick={() => onMove(-1)}>
            上移
          </Button>
          <Button size="small" type="link" disabled={!canWrite} onClick={() => onMove(1)}>
            下移
          </Button>
        </Space>
      </Space>
    </Card>
  );
}

export default function FlowTemplates(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canWrite = can(PermissionCode.FLOW_TEMPLATE_WRITE);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftNodes, setDraftNodes] = useState<FlowNode[] | null>(null);
  const [nodeModal, setNodeModal] = useState<{ node: FlowNode | null; mode: 'create' | 'edit' } | null>(null);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [conditionRules, setConditionRules] = useState<
    Array<{ field: string; operator: string; value: string; logic: 'AND' | 'OR' }>
  >([]);

  const templates = useResource<FlowTemplate[]>(() => flowApi.templates(), {
    fallback: () => mockFlowTemplateList(),
  });

  const list = templates.data ?? [];

  /**
   * 刚保存/刚新建出来的那一套。
   *
   * 【为什么需要它】保存会生成**新版本 = 新的一行 = 新的 id**，而列表要等下一次
   * 请求回来才包含它。这中间的一两帧里 `list.find(id)` 找不到那个 id，
   * 页面会退到 `list[0]`——表现为"刚点完保存，节点列表闪了一下别的流程"。
   * 把响应里的那一份兜在这里，等列表追上（同一个 id）自然让位。
   */
  const [savedTemplate, setSavedTemplate] = useState<FlowTemplate | null>(null);
  const current = useMemo(
    () =>
      savedTemplate?.id === selectedId
        ? savedTemplate
        : list.find((t) => t.id === selectedId) ?? list[0] ?? null,
    [list, selectedId, savedTemplate],
  );

  const nodes: FlowNode[] = draftNodes ?? current?.nodes ?? [];

  /* 条件分支能用的字段**跟着当前模板的业务类型走**：引擎按变量名取值，
   * 变量不存在的规则永远为假，界面上却看不出异常。 */
  const conditionFields = conditionFieldsOf(current?.bizType);
  const conditionFieldOptions = conditionFields.map((field) => ({
    value: field,
    label: CONDITION_FIELD_LABELS[field] ?? field,
  }));

  const startEdit = (): void => {
    if (!current) return;
    setDraftNodes([...(current.nodes ?? [])]);
    feedback.info('进入编辑模式：调整后点击「保存流程」才会提交');
  };

  const cancelEdit = (): void => {
    setDraftNodes(null);
  };

  const moveNode = (index: number, direction: -1 | 1): void => {
    const next = [...nodes];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    setDraftNodes(next.map((node, i) => ({ ...node, sortOrder: i + 1 })));
  };

  const removeNode = (index: number): void => {
    setDraftNodes(nodes.filter((_n, i) => i !== index).map((node, i) => ({ ...node, sortOrder: i + 1 })));
  };

  const openNodeModal = (mode: 'create' | 'edit', node: FlowNode | null): void => {
    setNodeModal({ mode, node });
    const rules = (node?.condition?.rules ?? []).map((rule) => ({
      field: rule.field,
      operator: rule.operator,
      value: Array.isArray(rule.value) ? rule.value.join(',') : String(rule.value),
      logic: node?.condition?.logic ?? 'AND',
    }));
    setConditionRules(rules);
  };

  /**
   * 节点表单初值。**不要改回在 openNodeModal 里调 setFieldsValue**。
   * 弹窗带 `destroyOnHidden`，关闭时 `<Form>` 被卸载，此刻的同步赋值会被丢弃，
   * 重新挂载时又按 initialValues（空）初始化——表现为"点编辑节点，里面全空"。
   * 详见 `LegalWorkbench.tsx`。
   */
  const nodeFormInitialValues = useMemo(() => {
    const node = nodeModal?.node ?? null;
    return {
      key: node?.key ?? `node_${nodes.length + 1}`,
      name: node?.name ?? '',
      type: node?.type ?? FlowNodeType.APPROVAL,
      assigneeRoleCode: node?.assigneeRoleCode ?? undefined,
      requiredPermission: node?.requiredPermission ?? undefined,
      timeoutHours: node?.timeoutHours ?? 24,
      allowSpecialRelease: node?.allowSpecialRelease ?? false,
      description: node?.description ?? '',
      nextKey: node?.nextKey ?? undefined,
      defaultNextKey: node?.defaultNextKey ?? undefined,
      conditionLogic: node?.condition?.logic ?? 'AND',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeModal]);

  const saveNode = async (): Promise<void> => {
    const values = await form.validateFields();
    const type = values.type as FlowNodeType;
    const condition =
      type === FlowNodeType.CONDITION && conditionRules.length > 0
        ? {
            logic: (values.conditionLogic ?? 'AND') as 'AND' | 'OR',
            rules: conditionRules.map((rule) => ({
              field: rule.field as 'amount',
              operator: rule.operator as 'GT',
              value: rule.field === 'amount' ? Number(rule.value) : rule.value.includes(',') ? rule.value.split(',') : rule.value,
            })),
          }
        : null;

    const base: FlowNode = {
      id: nodeModal?.node?.id ?? `draft-${Date.now()}`,
      templateId: current?.id ?? 'draft',
      key: String(values.key ?? ''),
      name: String(values.name ?? ''),
      type,
      sortOrder: nodeModal?.node?.sortOrder ?? nodes.length + 1,
      assigneeRoleCode: values.assigneeRoleCode ? String(values.assigneeRoleCode) : null,
      requiredPermission: values.requiredPermission ? String(values.requiredPermission) : null,
      condition,
      nextKey: values.nextKey ? String(values.nextKey) : null,
      defaultNextKey: values.defaultNextKey ? String(values.defaultNextKey) : null,
      timeoutHours: values.timeoutHours === undefined || values.timeoutHours === null ? null : Number(values.timeoutHours),
      allowSpecialRelease: Boolean(values.allowSpecialRelease),
      description: values.description ? String(values.description) : null,
    };

    setDraftNodes((prev) => {
      const source = prev ?? nodes;
      if (nodeModal?.mode === 'edit' && nodeModal.node) {
        return source.map((node) => (node.id === nodeModal.node?.id ? { ...base, id: node.id } : node));
      }
      return [...source, base];
    });
    setNodeModal(null);
  };

  /**
   * 保存流程。
   *
   * 【后端保存的是「新版本」，不是原地改】
   * 在办单据按模板 ID 实时读节点，原地改会让它们改走新流程、甚至卡在
   * 已被删掉的节点上。所以服务端每次保存生成一个新版本，在办单据留在旧版本上——
   * 保存后 `current.id` 会变成一个**新的模板 ID**，这里必须跟着切过去，
   * 否则页面上会显示一个"编辑中"的幽灵（列表已重新加载，选中的 id 已不在其中）。
   */
  const saveTemplate = async (): Promise<void> => {
    if (!current) return;
    const payload: FlowTemplateNodeInput[] = nodes.map((node, index) => ({
      key: node.key,
      name: node.name,
      type: node.type,
      sortOrder: index + 1,
      assigneeRoleCode: node.assigneeRoleCode,
      requiredPermission: node.requiredPermission,
      condition: node.condition as unknown as Record<string, unknown> | null,
      nextKey: node.nextKey,
      defaultNextKey: node.defaultNextKey,
      timeoutHours: node.timeoutHours,
    }));
    const fromVersion = current.version;
    const bizLabel = BIZ_TYPE_LABELS[current.bizType] ?? '单据';
    setSaving(true);
    const result = await performAction({
      action: () => flowApi.updateTemplate(current.id, { nodes: payload }),
      successMessage: (t) =>
        t.version === fromVersion
          ? '与当前版本一致，未生成新版本'
          : `已保存为 v${t.version}：新提交的${bizLabel}按新流程流转，在办的仍走 v${fromVersion}`,
    });
    setSaving(false);
    if (result.ok && result.data) {
      setDraftNodes(null);
      setSavedTemplate(result.data);
      setSelectedId(result.data.id);
      templates.reload();
    }
  };

  /** 新建模板：先建出一套「开始 → 审批 → 结束」的骨架，再进编辑模式细调 */
  const createTemplate = async (): Promise<void> => {
    const values = await createForm.validateFields();
    const bizType = String(values.bizType);
    setCreating(true);
    const result = await performAction({
      action: () =>
        flowApi.createTemplate({
          code: String(values.code).trim().toUpperCase(),
          name: String(values.name).trim(),
          description: values.description ? String(values.description) : undefined,
          bizType: bizType as BizType,
          nodes: starterNodes(bizType),
        }),
      successMessage: (t) =>
        t.isDefault
          ? `模板已创建，并设为${BIZ_TYPE_LABELS[t.bizType] ?? ''}的默认流程：之后提交的单据按它流转`
          : '模板已创建（该业务类型已有默认模板，这一套不会自动生效）',
    });
    setCreating(false);
    if (result.ok && result.data) {
      setCreateOpen(false);
      createForm.resetFields();
      setSavedTemplate(result.data);
      setSelectedId(result.data.id);
      templates.reload();
    }
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="流程模板"
        description="配置各业务类型的审批流程：节点、处理角色、条件分支与超时提醒。发票、合同、费用申请、费用报销各有一套默认模板，也可以自己新建。流程改变的是「谁审、审几级」，不能去掉人工确认环节。"
        extra={
          <Space>
            <Button onClick={templates.reload}>刷新</Button>
            {draftNodes ? (
              <>
                <Button onClick={cancelEdit}>取消编辑</Button>
                <Button type="primary" loading={saving} disabled={!canWrite} onClick={() => void saveTemplate()}>
                  保存流程
                </Button>
              </>
            ) : (
              <Button type="primary" disabled={!canWrite || !current} onClick={startEdit}>
                编辑流程
              </Button>
            )}
          </Space>
        }
      />

      <Alert
        type="warning"
        showIcon
        icon={<AuditOutlined />}
        style={{ marginBottom: 12 }}
        message="流程配置的两条红线"
        description={
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18, lineHeight: 1.9 }}>
            <li>
              <strong>人工初审节点不可删除</strong>：自动化校验只做风险标记，业务约束要求发票必须经财务人员人工确认后才能推送
              T+。条件分支可以决定由「谁」来审、金额大的是否需要加签，但不能把审批节点改成自动通过。
              <Typography.Text type="secondary">
                （这条由服务端在保存时校验：发票模板里必须留一个处理权限是「发票复核」的审批节点，
                删不掉也换不成自动节点）
              </Typography.Text>
            </li>
            <li>
              <strong>推送 T+ 节点不生成凭证</strong>：该节点只把业务数据放入 T+ 待办业务池，凭证由财务在 T+ 端选择会计科目生成。
            </li>
          </ul>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={7}>
          <Card
            size="small"
            title={
              <Space>
                模板列表
                <Button
                  size="small"
                  type="link"
                  icon={<PlusOutlined />}
                  disabled={!canWrite}
                  onClick={() => setCreateOpen(true)}
                >
                  新建模板
                </Button>
              </Space>
            }
          >
            <PageState loading={templates.loading} error={templates.error} onRetry={templates.reload} skeleton rows={5}>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {list.map((template) => (
                  <Card
                    key={template.id}
                    size="small"
                    hoverable
                    styles={{ body: { padding: 10 } }}
                    style={{
                      cursor: 'pointer',
                      borderColor: current?.id === template.id ? '#1677ff' : undefined,
                    }}
                    onClick={() => {
                      if (draftNodes) {
                        feedback.warning('请先保存或取消当前编辑，再切换模板');
                        return;
                      }
                      setSelectedId(template.id);
                    }}
                  >
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Space wrap size={4}>
                        <Typography.Text strong>{template.name}</Typography.Text>
                        <Tag color="geekblue">{BIZ_TYPE_LABELS[template.bizType] ?? template.bizType}</Tag>
                        {template.isDefault ? <Tag color="blue">默认</Tag> : null}
                        <Tag color={template.isActive ? 'green' : 'default'}>
                          {template.isActive ? '启用' : '停用'}
                        </Tag>
                      </Space>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {template.code} · v{template.version} · {template.nodes?.length ?? 0} 个节点
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {template.description ?? ''}
                      </Typography.Text>
                    </Space>
                  </Card>
                ))}
                {list.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无流程模板，可点上方「新建模板」建一套" />
                ) : null}
              </Space>
            </PageState>
          </Card>
        </Col>

        <Col xs={24} xl={17}>
          <Card
            size="small"
            title={
              <Space>
                <BranchesOutlined />
                {current ? `${current.name} —— 流程节点` : '流程节点'}
                {draftNodes ? <Tag color="orange">编辑中（未保存）</Tag> : null}
              </Space>
            }
            extra={
              draftNodes ? (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openNodeModal('create', null)}
                >
                  添加节点
                </Button>
              ) : null
            }
          >
            <PageState loading={templates.loading} error={templates.error} onRetry={templates.reload} skeleton rows={6}>
              {nodes.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该模板尚未配置节点" />
              ) : (
                <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                  <Space size={4} align="start" wrap>
                    {nodes.map((node, index) => (
                      <Space key={node.id} size={4} align="center">
                        <Space direction="vertical" size={4} align="center">
                          <NodeCard
                            node={node}
                            bizType={current?.bizType}
                            index={index}
                            canWrite={Boolean(draftNodes) && canWrite}
                            onEdit={() => openNodeModal('edit', node)}
                            onRemove={() => removeNode(index)}
                            onMove={(direction) => moveNode(index, direction)}
                          />
                        </Space>
                        {index < nodes.length - 1 ? (
                          <Space direction="vertical" size={0} align="center">
                            <ArrowRightOutlined style={{ color: 'rgba(0,0,0,0.35)' }} />
                            <ArrowDownOutlined style={{ color: 'rgba(0,0,0,0.15)', display: 'none' }} />
                          </Space>
                        ) : null}
                      </Space>
                    ))}
                  </Space>
                </div>
              )}
            </PageState>
          </Card>

          {current ? (
            <Card size="small" title="节点明细" style={{ marginTop: 16 }}>
              <Table<FlowNode>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={nodes}
                locale={{ emptyText: '暂无节点' }}
                columns={[
                  { title: '#', key: 'order', width: 50, render: (_v, _r, i) => i + 1 },
                  { title: '节点', dataIndex: 'name', width: 160 },
                  {
                    title: '类型',
                    dataIndex: 'type',
                    width: 110,
                    render: (v: FlowNodeType) => (
                      <Space size={4}>
                        {NODE_ICONS[v]}
                        <span>{FLOW_NODE_TYPE_LABELS[v]}</span>
                      </Space>
                    ),
                  },
                  {
                    title: '处理角色',
                    dataIndex: 'assigneeRoleCode',
                    width: 130,
                    render: (v: string | null) =>
                      v ? ROLE_LABELS[v] ?? v : <Typography.Text type="secondary">系统</Typography.Text>,
                  },
                  {
                    title: '条件 / 说明',
                    key: 'condition',
                    render: (_v, record) => (
                      <Space direction="vertical" size={2}>
                        {record.type === FlowNodeType.CONDITION ? (
                          <Tag color="purple">{describeCondition(record)}</Tag>
                        ) : null}
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {record.description ?? '-'}
                        </Typography.Text>
                      </Space>
                    ),
                  },
                  {
                    title: '超时',
                    dataIndex: 'timeoutHours',
                    width: 90,
                    render: (v: number | null) => (v ? `${v} 小时` : '-'),
                  },
                  {
                    title: '特例放行',
                    dataIndex: 'allowSpecialRelease',
                    width: 100,
                    render: (v: boolean) =>
                      v ? <Tag color="red">允许</Tag> : <Typography.Text type="secondary">不允许</Typography.Text>,
                  },
                ]}
              />
            </Card>
          ) : null}

          <Card size="small" title="条件分支说明" style={{ marginTop: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="可用条件字段">
                    {conditionFieldOptions.length > 0
                      ? conditionFieldOptions.map((o) => o.label).join(' / ')
                      : '当前模板的业务类型暂不支持条件分支'}
                  </Descriptions.Item>
                  <Descriptions.Item label="可用比较符">
                    {Object.values(OPERATOR_LABELS).join(' / ')}
                  </Descriptions.Item>
                  <Descriptions.Item label="组合方式">且（AND） / 或（OR）</Descriptions.Item>
                  <Descriptions.Item label="金额单位">以「分」比较，避免浮点误差</Descriptions.Item>
                </Descriptions>
              </Col>
              <Col xs={24} lg={12}>
                <Alert
                  type="info"
                  showIcon
                  message="典型配置示例"
                  description={
                    <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.9, fontSize: 12 }}>
                      {current?.bizType === BizType.INVOICE ? (
                        <>
                          <li>金额 &gt; 50 万 → 加签一级「财务负责人」审批（默认财务初审岗上限 50 万）</li>
                          <li>风险等级 = 高风险 → 强制走特例放行节点，要求填写专项审批依据</li>
                          <li>发票类型 = 数电票 → 跳过「发票代码校验」的提示文案</li>
                        </>
                      ) : (
                        <>
                          <li>
                            金额 &gt; 阈值 → 加签一级审批（如「{conditionFieldOptions.find((o) => o.value === 'amount')?.label ?? '金额'}{' '}
                            &gt; 500000」即 5000 元以上加签）
                          </li>
                          <li>类型分流 → 按业务类型走不同的审批节点（不必给每条路径配一份模板）</li>
                        </>
                      )}
                      <li>超时未处理 → 按节点设置的超时小时数提醒处理人与管理员</li>
                    </ul>
                  }
                />
              </Col>
            </Row>
          </Card>

          {/* 这张预览图讲的是发票的路径（自动校验、推送 T+、凭证）。
              其它业务类型下它只会误导人，所以只在发票模板上出现。 */}
          {current?.bizType === BizType.INVOICE ? (
          <Card size="small" title="流程效果预览" style={{ marginTop: 16 }}>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              以下为「一张普通发票」与「一张高风险大额发票」的流转路径示意，用于核对配置是否符合预期。
            </Typography.Paragraph>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space wrap size={4}>
                <Tag color="green">普通发票</Tag>
                <span>提交校验</span>
                <ArrowRightOutlined />
                <span>自动校验（OCR / 企查查 / 税务 / 合同）</span>
                <ArrowRightOutlined />
                <Tag color="orange">财务初审（人工）</Tag>
                <ArrowRightOutlined />
                <span>推送 T+ 待办业务池</span>
                <ArrowRightOutlined />
                <Tag color="blue">财务在 T+ 端选择科目生成凭证</Tag>
              </Space>
              <Space wrap size={4}>
                <Tag color="red">高风险 / 大额发票</Tag>
                <span>提交校验</span>
                <ArrowRightOutlined />
                <span>自动校验</span>
                <ArrowRightOutlined />
                <Tag color="purple">条件分支：风险=高 或 金额&gt;50万</Tag>
                <ArrowRightOutlined />
                <Tag color="orange">财务初审（人工）</Tag>
                <ArrowRightOutlined />
                <Tag color="red">特例放行（需专项审批依据）</Tag>
                <ArrowRightOutlined />
                <span>推送 T+ 待办业务池</span>
              </Space>
            </Space>
          </Card>
          ) : null}
        </Col>
      </Row>

      <Modal
        open={createOpen}
        title="新建流程模板"
        onCancel={() => setCreateOpen(false)}
        onOk={() => void createTemplate()}
        confirmLoading={creating}
        okText="创建"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="先建骨架，再细调节点"
          description="创建后按模板生成「开始 → 审批 → 结束」三个节点并进入编辑模式，可继续增删节点、配条件分支与超时。只有在该业务类型还没有默认模板时，新建的这套才会自动成为默认流程。"
        />
        <Form
          form={createForm}
          layout="vertical"
          preserve={false}
          initialValues={{ bizType: current?.bizType ?? BizType.INVOICE }}
        >
          <Form.Item
            name="bizType"
            label="业务类型"
            rules={[{ required: true, message: '请选择业务类型' }]}
            extra="决定这套模板在提交什么单据时被选中；创建后不可更改"
          >
            <Select
              options={FLOW_BACKED_BIZ_TYPES.map((b) => ({ value: b, label: BIZ_TYPE_LABELS[b] ?? b }))}
            />
          </Form.Item>
          <Form.Item
            name="code"
            label="模板编码"
            rules={[
              { required: true, message: '请输入模板编码' },
              {
                pattern: /^[A-Za-z][A-Za-z0-9_]{1,63}$/,
                message: '字母开头，仅含字母、数字与下划线',
              },
            ]}
            extra="同一租户内唯一，创建后不可改（改流程会生成新版本，编码不变）"
          >
            <Input className="ilc-mono" placeholder="MY_INVOICE_FLOW" />
          </Form.Item>
          <Form.Item name="name" label="模板名称" rules={[{ required: true, message: '请输入模板名称' }]}>
            <Input placeholder="如 发票审批（简化版）" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} placeholder="这套流程与默认流程的差别，便于同事理解" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={nodeModal !== null}
        title={nodeModal?.mode === 'edit' ? `编辑节点 ${nodeModal.node?.name ?? ''}` : '添加节点'}
        onCancel={() => setNodeModal(null)}
        onOk={() => void saveNode()}
        width={680}
        destroyOnHidden
        okText="确定"
      >
        <Form
          key={`${nodeModal?.mode ?? 'none'}-${nodeModal?.node?.key ?? 'new'}`}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={nodeFormInitialValues}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="key"
                label="节点标识（key）"
                rules={[
                  { required: true, message: '请输入节点标识' },
                  { pattern: /^[a-z][a-z0-9_]{2,31}$/, message: '小写字母开头，仅含小写字母、数字与下划线' },
                ]}
                extra="模板内唯一，条件分支跳转按此标识定位"
              >
                <Input className="ilc-mono" placeholder="finance_review" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="节点名称" rules={[{ required: true, message: '请输入节点名称' }]}>
                <Input placeholder="如 财务初审" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="节点类型" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(FLOW_NODE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="assigneeRoleCode" label="处理角色（人工审批节点）">
                <Select
                  allowClear
                  placeholder="选择处理角色"
                  options={ROLE_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="requiredPermission" label="所需权限码（可选）" extra="节点处理人必须持有该权限">
                <Select
                  allowClear
                  showSearch
                  placeholder="选择权限码"
                  options={[
                    { value: PermissionCode.INVOICE_REVIEW, label: '财务初审 invoice:review' },
                    { value: PermissionCode.INVOICE_SPECIAL_RELEASE, label: '特例放行 invoice:special-release' },
                    { value: PermissionCode.TPLUS_PUSH, label: '推送T+ tplus:push' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="timeoutHours" label="超时提醒（小时）" extra="超时后提醒处理人与管理员">
                <InputNumber style={{ width: '100%' }} min={1} max={720} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nextKey" label="下一节点 key">
                <Select
                  allowClear
                  placeholder="按顺序流转可留空"
                  options={nodes.map((n) => ({ value: n.key, label: `${n.name}（${n.key}）` }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="defaultNextKey" label="条件未命中时的默认节点">
                <Select
                  allowClear
                  placeholder="条件分支专用"
                  options={nodes.map((n) => ({ value: n.key, label: `${n.name}（${n.key}）` }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="allowSpecialRelease" label="允许特例放行" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="节点说明（展示给操作人）">
                <Input.TextArea rows={2} placeholder="如：审阅校验报告，判断票面与业务真实性" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            条件规则（仅条件分支节点使用）
          </Divider>
          <Form.Item name="conditionLogic" label="条件组合方式">
            <Select
              options={[
                { value: 'AND', label: '全部满足（AND）' },
                { value: 'OR', label: '任一满足（OR）' },
              ]}
            />
          </Form.Item>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {conditionRules.map((rule, index) => (
              <Space key={index} wrap>
                <Select
                  style={{ width: 140 }}
                  value={rule.field}
                  onChange={(value) => {
                    const next = [...conditionRules];
                    next[index] = { ...rule, field: value };
                    setConditionRules(next);
                  }}
                  options={conditionFieldOptions}
                />
                <Select
                  style={{ width: 120 }}
                  value={rule.operator}
                  onChange={(value) => {
                    const next = [...conditionRules];
                    next[index] = { ...rule, operator: value };
                    setConditionRules(next);
                  }}
                  options={Object.entries(OPERATOR_LABELS).map(([value, label]) => ({ value, label }))}
                />
                <Input
                  style={{ width: 220 }}
                  value={rule.value}
                  placeholder={rule.field === 'amount' ? '金额（分），如 5000000' : '值，多个用逗号分隔'}
                  onChange={(e) => {
                    const next = [...conditionRules];
                    next[index] = { ...rule, value: e.target.value };
                    setConditionRules(next);
                  }}
                />
                <Button danger size="small" onClick={() => setConditionRules(conditionRules.filter((_r, i) => i !== index))}>
                  删除
                </Button>
              </Space>
            ))}
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setConditionRules([...conditionRules, { field: 'amount', operator: 'GT', value: '', logic: 'AND' }])
              }
            >
              添加条件
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
